import { useState, useCallback, useRef, useMemo, useEffect, useLayoutEffect } from 'react'
import { gridGalleryConfig } from '../config/gridGalleryConfig'

interface UseGridVirtualizationOptions {
  imageCount: number
  columns: number
  gap: number
  containerRef: React.RefObject<HTMLDivElement>
}

interface VisibleRange {
  startIndex: number
  endIndex: number
}

interface UseGridVirtualizationReturn {
  visibleRange: VisibleRange
  totalHeight: number
  offsetTop: number
  visibleCount: number
  rowHeight: number
  onScroll: () => void
}

/**
 * Virtualization hook for a uniform grid layout.
 * All cells share gridGalleryConfig.cellAspectRatio (9:16 portrait), so
 * row height = cell width / ratio.
 * Uses O(1) arithmetic to calculate which rows are visible.
 */
export function useGridVirtualization({
  imageCount,
  columns,
  gap,
  containerRef,
}: UseGridVirtualizationOptions): UseGridVirtualizationReturn {
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  )
  const [containerWidth, setContainerWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )

  const rafRef = useRef<number | null>(null)

  // Initialize from actual container dimensions on mount and when container changes
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateDimensions = () => {
      setContainerWidth(container.clientWidth)
      setViewportHeight(container.clientHeight)
    }

    updateDimensions()

    // Also update on resize
    const resizeObserver = new ResizeObserver(updateDimensions)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [containerRef])

  // Sync scrollTop when columns/gap change (after zoom).
  // useLayoutEffect so it runs AFTER useGridZoom's layout effect sets scrollTop,
  // but BEFORE paint — the virtualization re-renders with correct visible range.
  useLayoutEffect(() => {
    const container = containerRef.current
    if (container) {
      setScrollTop(container.scrollTop)
    }
  }, [columns, gap, containerRef])

  // Cell width and row height (portrait cells: aspect-ratio = width/height)
  const cellWidth = useMemo(() => {
    return (containerWidth - Math.max(0, columns - 1) * gap) / columns
  }, [containerWidth, columns, gap])

  // Cell height = cellWidth / aspectRatio (e.g. 3/4 ratio -> height is 4/3 of width)
  const cellHeight = cellWidth / gridGalleryConfig.cellAspectRatio
  const rowHeight = cellHeight + gap

  // Total rows and total height
  const totalRows = Math.ceil(imageCount / columns)
  const totalHeight = totalRows > 0 ? totalRows * rowHeight - gap : 0

  // Buffer in pixels
  const bufferPx = gridGalleryConfig.virtualization.bufferRows * rowHeight

  // Visible row range
  const firstVisibleRow = Math.max(0, Math.floor((scrollTop - bufferPx) / rowHeight))
  const lastVisibleRow = Math.min(
    totalRows - 1,
    Math.ceil((scrollTop + viewportHeight + bufferPx) / rowHeight)
  )

  const visibleRange: VisibleRange = {
    startIndex: firstVisibleRow * columns,
    endIndex: Math.min((lastVisibleRow + 1) * columns, imageCount),
  }

  // Offset for the top spacer (position the visible items correctly)
  const offsetTop = firstVisibleRow * rowHeight

  // How many images fit on the actual screen (for quality tier selection)
  const visibleRows = Math.max(1, Math.ceil(viewportHeight / rowHeight))
  const visibleCount = visibleRows * columns

  // Scroll handler with rAF throttle
  const onScroll = useCallback(() => {
    if (rafRef.current !== null) return

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const container = containerRef.current
      if (!container) return

      setScrollTop(container.scrollTop)
    })
  }, [containerRef])

  return {
    visibleRange,
    totalHeight: Math.max(0, totalHeight),
    offsetTop,
    visibleCount,
    rowHeight,
    onScroll,
  }
}
